
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { ApiError } from '@empathyco/playboard-core-domain'
import type { PropType, Ref, SetupContext } from 'vue'
import type { IndexClientFeedDTO, RegularFilter } from '../../models'
import type {
  IndexJobServiceListFilters,
  IndexJobServiceListFilterState,
} from '../../services/interfaces/index-job.service'

import { FilterTypeEnum } from '@empathybroker/tsclient-play/dist/enums/filter-type.enum'

import { computed, defineComponent, getCurrentInstance, onMounted, ref } from 'vue'

import { mapActions, useApiError } from '../../composables'

import { INDEX_CONSTS } from '../../store/index/index.consts'

import EpbAside from '../aside.component.vue'
import RegularFilterSelector from '../filters/regular-filter-selector.component.vue'
import { EpbButton } from '../shared/epb-button'

export default defineComponent({
  name: 'IndexJobFilters',
  components: {
    EpbAside,
    EpbButton,
    RegularFilterSelector,
  },
  props: {
    instances: {
      type: Array as PropType<Array<Instance>>,
      required: true,
    },
    filters: {
      type: Object as PropType<IndexJobServiceListFilters>,
      required: true,
    },
  },
  setup(
    props: {
      instances: Array<Instance>
      filters: IndexJobServiceListFilters
    },
    { emit }: SetupContext,
  ) {
    const {
      proxy: { $epb, $store },
    } = getCurrentInstance()

    const { getErrorMessage } = useApiError()

    const baseClass = 'epb-index-jobs-filters'

    const messages = $epb.messages.tools.index

    const asideOpen = ref(false)

    const feedId: Ref<Array<IndexClientFeedDTO>> = ref([])

    const { getClient } = mapActions($store, INDEX_CONSTS.moduleName, {
      getClient: INDEX_CONSTS.actions.getClient,
    })

    const filtersSelected: Ref<IndexJobServiceListFilters> = ref({})

    const instancesFilters = ref({
      config: {
        id: 'instance',
        prettyName: messages.fields.clientId.label,
        type: FilterTypeEnum.REGULAR,
        values: [],
      },
      selectedValues: [],
    })

    const statusFilters = ref({
      config: {
        id: 'state',
        prettyName: messages.fields.state.label,
        type: FilterTypeEnum.REGULAR,
        values: [
          { id: 'FAILURE' as IndexJobServiceListFilterState },
          { id: 'QUEUED' as IndexJobServiceListFilterState },
          { id: 'DISCARDED' as IndexJobServiceListFilterState },
        ],
      },
      selectedValues: [],
    })

    const feedIdFilters = ref({
      config: {
        id: 'feedId',
        prettyName: messages.fields.feedId.label,
        type: FilterTypeEnum.REGULAR,
        values: [],
      },
      selectedValues: [],
    })

    const removeRepeatItems = (): void => {
      const setObj = new Set()
      feedIdFilters.value.config.values = feedIdFilters.value.config.values.reduce(
        (acc, feedId) => {
          if (!setObj.has(feedId.id)) {
            setObj.add(feedId.id)
            acc.push(feedId)
          }
          return acc
        },
        [],
      )
    }

    async function getFeedsId(instanceId: string): Promise<void> {
      try {
        const response = await getClient(instanceId)
        feedId.value =
          props.instances.length <= 1 ? response.feeds : feedId.value.concat(response.feeds)
      } catch (error) {
        emit(
          'error',
          getErrorMessage({
            ...error,
          } as ApiError),
        )
      }
    }

    const allFilters = computed((): Array<RegularFilter> => {
      if (props.instances.length <= 1) {
        getFeedsId(props.instances[0].id)
      }
      feedId.value.forEach(feed => {
        feedIdFilters.value.config.values.push({ id: feed.id })
      })
      removeRepeatItems()

      return props.instances.length > 1
        ? [instancesFilters.value, feedIdFilters.value, statusFilters.value]
        : [feedIdFilters.value, statusFilters.value]
    })

    const open = (): void => {
      asideOpen.value = true
    }

    const close = (): void => {
      asideOpen.value = false
    }

    const onFilterChange = (filter: RegularFilter, selectedValue: string[]): void => {
      if (filter.config.prettyName.includes(instancesFilters.value.config.prettyName)) {
        instancesFilters.value.selectedValues = selectedValue
        feedIdFilters.value.config.values.splice(0, feedIdFilters.value.config.values.length)
        feedId.value.splice(0, feedId.value.length)
        instancesFilters.value.selectedValues.forEach(instanceSelected => {
          getFeedsId(instanceSelected)
        })
      } else if (filter.config.prettyName.includes(statusFilters.value.config.prettyName)) {
        statusFilters.value.selectedValues = selectedValue
      } else {
        feedIdFilters.value.selectedValues = selectedValue
      }
      if (props.instances.length > 1 && instancesFilters.value.selectedValues.length === 0) {
        feedIdFilters.value.selectedValues.splice(0, feedIdFilters.value.selectedValues.length)
      }
    }

    const apply = (): void => {
      filtersSelected.value.clientIds =
        props.instances.length > 1
          ? [...instancesFilters.value.selectedValues]
          : [props.instances[0].id]
      filtersSelected.value.feedIds = [...feedIdFilters.value.selectedValues]
      filtersSelected.value.states = [...statusFilters.value.selectedValues]
      emit('apply', filtersSelected.value)
      close()
    }

    const isFilterDisabled = (filter: RegularFilter): boolean => {
      return (
        filter.config.prettyName === feedIdFilters.value.config.prettyName &&
        instancesFilters.value.selectedValues.length === 0 &&
        props.instances.length > 1
      )
    }

    onMounted((): void => {
      props.instances.forEach(instance => {
        instancesFilters.value.config.values.push({ id: instance.id })
        if (props.filters.clientIds?.includes(instance.id)) {
          instancesFilters.value.selectedValues.push({ id: instance.id })
        }
      })
      statusFilters.value.config.values.forEach(state => {
        if (props.filters.states?.includes(state.id)) {
          statusFilters.value.selectedValues.push({ id: state.id })
        }
      })
    })

    return {
      baseClass,
      messages,
      asideOpen,
      open,
      close,
      allFilters,
      onFilterChange,
      isFilterDisabled,
      apply,
    }
  },
})
